import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Tasks.css'; // Reuse current Task styles

interface Task {
    _id: string;
    title: string;
    description?: string;
    dueDate?: string;
    priority: 'low' | 'medium' | 'high';
    status: 'pending' | 'completed' | 'on-hold';
    createdAt: string;
}

const GuestTasks: React.FC = () => {
    const { accessToken, loading: authLoading } = useAuth();
    const history = useHistory();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchTasks = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/tasks/assigned`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (res.ok) {
                setTasks(data.tasks);
            }
        } catch (err) {
            console.error('Error fetching guest tasks:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchTasks();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history]);

    const updateStatus = async (id: string, newStatus: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tasks/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            if (response.ok) {
                fetchTasks();
            }
        } catch (err) {
            alert('Error updating task status');
        }
    };

    if (authLoading || loading) return <div className="tasks-container">Loading your tasks...</div>;

    const pendingTasks = tasks.filter(t => t.status === 'pending');
    const onHoldTasks = tasks.filter(t => t.status === 'on-hold');
    const completedTasks = tasks.filter(t => t.status === 'completed');

    return (
        <div className="guest-feed-page tasks-container">
            <header className="feed-header">
                <div className="header-content">
                    <h1>My Tasks</h1>
                    <p>Helpful things assigned to you by the Couple</p>
                </div>
            </header>

            <main className="feed-container">
                {/* Pending Section */}
                <section className="task-list-section">
                    <h2>Pending Assignments</h2>
                    <div className="tasks-grid">
                        {pendingTasks.length === 0 ? (
                            <div className="empty-tasks-guest">
                                <span className="empty-icon">🎖️</span>
                                <p>No pending tasks! You're all caught up.</p>
                            </div>
                        ) : (
                            pendingTasks.map(task => (
                                <div key={task._id} className={`task-card priority-${task.priority}`}>
                                    <div className="task-header">
                                        <h3>{task.title}</h3>
                                    </div>
                                    <p className="task-desc">{task.description}</p>
                                    <div className="task-footer">
                                        {task.dueDate && <span className="due-date">📅 {new Date(task.dueDate).toLocaleDateString()}</span>}
                                        <span className={`priority-tag ${task.priority}`}>{task.priority}</span>
                                    </div>
                                    <div className="task-button-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => updateStatus(task._id, 'completed')}
                                            style={{
                                                flex: 2,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                border: 'none',
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                color: 'white',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Complete
                                        </button>
                                        <button
                                            onClick={() => updateStatus(task._id, 'on-hold')}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                background: 'rgba(245, 158, 11, 0.05)',
                                                color: '#f59e0b',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                border: '1px solid rgba(245, 158, 11, 0.2)',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Hold
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* On Hold Section */}
                {onHoldTasks.length > 0 && (
                    <section className="task-list-section" style={{ marginTop: '3rem' }}>
                        <h2 style={{ color: '#f59e0b' }}>On Hold</h2>
                        <div className="tasks-grid">
                            {onHoldTasks.map(task => (
                                <div key={task._id} className="task-card on-hold" style={{ borderLeft: '4px solid #f59e0b' }}>
                                    <div className="task-header">
                                        <h3>{task.title}</h3>
                                    </div>
                                    <p className="task-desc">{task.description}</p>
                                    <div className="task-button-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => updateStatus(task._id, 'completed')}
                                            style={{
                                                flex: 2,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                border: 'none',
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                color: 'white',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                                            }}
                                        >
                                            Complete
                                        </button>
                                        <button
                                            onClick={() => updateStatus(task._id, 'pending')}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                background: 'rgba(59, 130, 246, 0.05)',
                                                color: '#3b82f6',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                border: '1px solid rgba(59, 130, 246, 0.2)'
                                            }}
                                        >
                                            Resume
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Completed Section */}
                <section className="task-list-section" style={{ marginTop: '3rem' }}>
                    <h2>Completed</h2>
                    <div className="tasks-grid">
                        {completedTasks.length === 0 ? (
                            <p className="no-tasks">No completed tasks yet.</p>
                        ) : (
                            completedTasks.map(task => (
                                <div key={task._id} className="task-card completed">
                                    <div className="task-header">
                                        <h3>{task.title}</h3>
                                        <button
                                            onClick={() => updateStatus(task._id, 'pending')}
                                            style={{
                                                padding: '5px 12px',
                                                fontSize: '0.75rem',
                                                borderRadius: '6px',
                                                border: '1px solid #667eea',
                                                background: 'rgba(102, 126, 234, 0.05)',
                                                color: '#667eea',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                marginLeft: 'auto'
                                            }}
                                        >
                                            Undo
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default GuestTasks;

